import React from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { BtnV2, Icon, l } from '@manychat/manyui'

import {
  DisableScenarioConfirm,
  useAutomationStatusToggle,
} from 'apps/aiAutomation/components/DisableScenarioConfirm'
import { HintCard } from 'apps/aiAutomation/components/HintCard'
import { EmptyState } from 'apps/aiAutomation/components/PositiveCommentReplies/components/EmptyState'
import { SidebarHeader } from 'apps/aiAutomation/components/SidebarHeader/SidebarHeader'
import { AiAutomationAnalytics } from 'apps/aiAutomation/lib/analytics'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import {
  AiAlwaysOnAutomationsActions,
  AiAlwaysOnAutomationsSelectors,
} from 'apps/aiAutomation/store/core'
import { LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import {
  AiAutomationScenario,
  AiAutomationTovState,
} from 'shared/api/requests/aiAutomation/schemas'

import cm from './PositiveCommentReplies.module.css'

export const PositiveCommentReplies = () => {
  const dispatch = useAppDispatch()

  const isSocialEngagementPublished = useAppSelector(
    AiAlwaysOnAutomationsSelectors.getIsSocialEngagementPublished,
  )
  const isStatusUpdating =
    useAppSelector(AiAlwaysOnAutomationsSelectors.getLoaderStatus) ===
    LoaderStatus.SOCIAL_ENGAGEMENT_STATUS_UPDATING
  const tovState = useAppSelector(AiAlwaysOnAutomationsSelectors.getTovState)

  const { toggleStatus, isConfirmModalOpen, closeConfirmModal } = useAutomationStatusToggle({
    scenario: AiAutomationScenario.SOCIAL_ENGAGEMENT,
    source: 'automation_editor',
  })

  const reinitSetup = () => {
    if (tovState === AiAutomationTovState.ACTIVE) {
      AiAutomationAnalytics.logTovRateProcessStarted({
        is_first_generation: false,
      })

      dispatch(AiAlwaysOnAutomationsActions.startTovSetupFx())
    }

    dispatch(AiAutomationLayoutActions.viewOpened({ view: SidebarViewType.TOV_SETUP }))
  }

  const isTovGenerated = tovState === AiAutomationTovState.ACTIVE

  return (
    <section>
      <SidebarHeader
        title={l.translate('Nice Comment Responder')}
        button={
          <BtnV2
            onClick={toggleStatus}
            data-test-id="ai-automation/positive-comment-replies-status-change-button"
            variant={isSocialEngagementPublished ? 'danger-light' : 'primary'}
            loading={isStatusUpdating}
            disabled={isSocialEngagementPublished ? false : !isTovGenerated}
          >
            {isSocialEngagementPublished ? l.translate('Stop') : l.translate('Go Live')}
          </BtnV2>
        }
      />

      <div className={cm.content}>
        <div className="m-b-xl">
          <HintCard
            title={l.translate('AI will reply like you when')}
            icon="IncreaseEngagement"
            text={l.translate('Someone leaves a nice comment')}
          />
        </div>

        {isTovGenerated && (
          <div>
            <h4 className="m-b text-subheading">{l.translate('Tone setup completed')}</h4>

            <div className={cm.completedSetup}>
              <Icon.DoubleTick className="m-r-xs" />

              <p className="m-0">{l.translate('Ready to reply like you')}</p>

              <BtnV2 onClick={reinitSetup} variant="ghost-light">
                {l.translate('Adjust tone')}
              </BtnV2>
            </div>
          </div>
        )}

        {!isTovGenerated && <EmptyState />}
      </div>

      {isConfirmModalOpen && (
        <DisableScenarioConfirm
          onClose={closeConfirmModal}
          scenario={AiAutomationScenario.SOCIAL_ENGAGEMENT}
          source="automation_editor"
        />
      )}
    </section>
  )
}
